// src/utils/projectApi.ts - Updated with note editing functionality
import { authenticatedFetch } from './authApi';

// Projects API
export const getProjectsList = async () => {
  const response = await authenticatedFetch('/api/projects');
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get projects');
  }
  
  return response.json();
};

export const getProjectDetails = async (projectId: string) => {
  const response = await authenticatedFetch(`/api/projects/${projectId}`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get project details');
  }
  
  return response.json();
};

export const createProject = async (projectData: { title: string; description: string }) => {
  const response = await authenticatedFetch('/api/projects', {
    method: 'POST',
    body: JSON.stringify(projectData),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create project');
  }
  
  return response.json();
};

export const updateProject = async (projectId: string, projectData: { title: string; description?: string }) => {
  const response = await authenticatedFetch(`/api/projects/${projectId}`, {
    method: 'PUT',
    body: JSON.stringify(projectData),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update project');
  }
  
  return response.json();
};

export const deleteProject = async (projectId: string) => {
  const response = await authenticatedFetch(`/api/projects/${projectId}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete project');
  }
  
  return response.json();
};

// Notes API
export const getProjectNotes = async (projectId: string) => {
  const response = await authenticatedFetch(`/api/projects/${projectId}/notes`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get project notes');
  }
  
  return response.json();
};

export const addProjectNote = async (projectId: string, noteData: { title: string; content: string }) => {
  const response = await authenticatedFetch(`/api/projects/${projectId}/notes`, {
    method: 'POST',
    body: JSON.stringify({
      text: noteData.content, // Backend expects 'text', not 'content'
      author: noteData.title || 'User' // Backend expects 'author', not 'title'
    }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add note');
  }
  
  return response.json();
};

export const updateProjectNote = async (projectId: string, noteId: string, noteData: { title: string; content: string }) => {
  const response = await authenticatedFetch(`/api/projects/${projectId}/notes/${noteId}`, {
    method: 'PUT',
    body: JSON.stringify({
      text: noteData.content // Backend expects 'text', not 'content'
    }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update note');
  }
  
  return response.json();
};

export const deleteProjectNote = async (projectId: string, noteId: string) => {
  const response = await authenticatedFetch(`/api/projects/${projectId}/notes/${noteId}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete note');
  }
  
  return response.json();
};

// Tags API
export const getProjectTags = async (projectId: string) => {
  const response = await authenticatedFetch(`/api/projects/${projectId}/tags`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get project tags');
  }
  
  return response.json();
};

export const addProjectTag = async (projectId: string, tag: string) => {
  const response = await authenticatedFetch(`/api/projects/${projectId}/tags`, {
    method: 'POST',
    body: JSON.stringify({ tag }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add tag');
  }
  
  return response.json();
};

export const removeProjectTag = async (projectId: string, tag: string) => {
  const response = await authenticatedFetch(`/api/projects/${projectId}/tags/${encodeURIComponent(tag)}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove tag');
  }
  
  return response.json();
};

// Timeline API
export const getProjectTimeline = async (projectId: string) => {
  const response = await authenticatedFetch(`/api/projects/${projectId}/timeline`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get project timeline');
  }
  
  return response.json();
};

export const addTimelineEvent = async (projectId: string, eventData: { action: string; metadata?: Record<string, any> }) => {
  const response = await authenticatedFetch(`/api/projects/${projectId}/timeline`, {
    method: 'POST',
    body: JSON.stringify(eventData),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add timeline event');
  }
  
  return response.json();
};